"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";

import { objectToFormData } from "@/libs/shared/api/objectToFormData";
import type {
  CreateGroupModelProps,
  EditGroupModelProps,
} from "./groups.types";

const tagTypes = [
  "Assignments" as const,
  "Group" as const,
  "Groups" as const,
  "Members" as const,
  "TrainingPlans" as const,
  "Subscription" as const,
  "Vod" as const,
  "Playlist" as const,
];

export const invalidateGroups = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(groupsApi.util.invalidateTags([tag]));
      });
    });
  };
};

export const groupsApi = createApi({
  reducerPath: "groupsApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getGroups: builder.query({
      query: ({
        perPage,
        page,
        name,
        orderBy,
        orderByDirection,
      }: {
        perPage: number;
        page: string;
        name: string;
        orderBy: string;
        orderByDirection: string;
      }) => ({
        url: `pt/groups?per_page=${perPage}&page=${page}&name=${name}&order_by=${orderBy}&order_by_direction=${orderByDirection}`,
      }),
      providesTags: ["Groups"],
    }),
    getSubscription: builder.query({
      query: () => ({
        url: `pt/account/subscription`,
      }),
      providesTags: ["Subscription"],
    }),
    getGroup: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `pt/groups/${id}`,
      }),
      providesTags: ["Group"],
    }),
    generateCode: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `pt/groups/${id}/code`,
        method: "POST",
      }),
      onQueryStarted: invalidateGroups(["Group", "Groups"]),
    }),
    deleteGroup: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: `pt/groups/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: invalidateGroups(["Groups"]),
    }),
    deleteGroups: builder.mutation({
      query: ({ groups }: { groups: { group_id: string } }) => {
        return {
          url: `pt/groups?group_id=${groups.group_id}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateGroups(["Groups"]),
    }),
    createGroup: builder.mutation({
      query: (group: CreateGroupModelProps) => {
        const formData = objectToFormData(group);
        return {
          url: `pt/groups`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateGroups(["Groups"]),
    }),
    editGroup: builder.mutation({
      query: (group: EditGroupModelProps) => {
        const formData = objectToFormData(group);
        return {
          url: `pt/groups/${group.id}`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: invalidateGroups(["Group", "Groups"]),
    }),
    getEmails: builder.query({
      query: ({ group_id }: { group_id: string }) => ({
        url: `pt/groups/emails?group_id=${group_id}`,
      }),
    }),
    getMembers: builder.query({
      query: ({
        id,
        page,
        perPage,
        name,
      }: {
        id: string;
        page: number;
        perPage: number;
        name: string;
      }) => ({
        url: `pt/groups/${id}/members?page=${page}&per_page=${perPage}&name=${encodeURIComponent(name)}`,
      }),
      providesTags: ["Members"],
      // Infinite-scroll pattern: stessa cache-key per (id, name); ogni page
      // successiva concatena, page === 1 resetta (anche dopo invalidate).
      serializeQueryArgs: ({ queryArgs }) => ({
        id: queryArgs.id,
        name: queryArgs.name,
      }),
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1 || !currentCache?.members?.data) {
          currentCache.members = newItems.members;
          currentCache.rest = newItems.rest;
          return;
        }
        currentCache.members.data.push(...newItems.members.data);
        currentCache.members.current_page = newItems.members.current_page;
        currentCache.members.last_page = newItems.members.last_page;
        currentCache.members.total = newItems.members.total;
        currentCache.rest.data.push(...newItems.rest.data);
        currentCache.rest.current_page = newItems.rest.current_page;
        currentCache.rest.last_page = newItems.rest.last_page;
        currentCache.rest.total = newItems.rest.total;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page ||
        currentArg?.name !== previousArg?.name ||
        currentArg?.id !== previousArg?.id,
    }),
    addMember: builder.mutation({
      query: ({
        customerId,
        groupId,
      }: {
        customerId: number;
        groupId: number;
      }) => {
        return {
          url: `pt/customers/${customerId}/groups/${groupId}`,
          method: "POST",
        };
      },
      // Niente invalidate "Members": un refetch sulla page corrente
      // duplicherebbe gli item (concat). Lo sposto a mano via
      // updateQueryData lato componente per preservare l'accumulo.
    }),
    removeMember: builder.mutation({
      query: ({
        customerId,
        groupId,
      }: {
        customerId: number;
        groupId: number;
      }) => {
        return {
          url: `pt/customers/${customerId}/groups/${groupId}`,
          method: "DELETE",
        };
      },
    }),
  }),
});

export const {
  useGetGroupsQuery,
  useGetGroupQuery,
  useGenerateCodeMutation,
  useGetSubscriptionQuery,
  useDeleteGroupsMutation,
  useCreateGroupMutation,
  useGetEmailsQuery,
  useEditGroupMutation,
  useGetMembersQuery,
  useAddMemberMutation,
  useRemoveMemberMutation,
} = groupsApi;
